class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.handleCartLinkClick = this.handleCartLinkClick.bind(this);
    this.handleCartLinkKeydown = this.handleCartLinkKeydown.bind(this);
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    document.addEventListener('click', this.handleCartLinkClick);
    document.addEventListener('keydown', this.handleCartLinkKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleCartLinkClick);
    document.removeEventListener('keydown', this.handleCartLinkKeydown);
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('#cart-icon-bubble, [data-sc-cart-link]').forEach((cartLink) => {
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
    });
  }

  handleCartLinkClick(event) {
    const cartLink = event.target.closest('#cart-icon-bubble, [data-sc-cart-link]');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  handleCartLinkKeydown(event) {
    const cartLink = event.target.closest('#cart-icon-bubble, [data-sc-cart-link]');
    if (!cartLink || event.code.toUpperCase() !== 'SPACE') return;
    event.preventDefault();
    this.open(cartLink);
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        /* FIX (SOLCOZM Custom, 2026-08-10): this transitionend listener is
           registered with {once:true} inside open(), and open() itself
           early-returns while the drawer is already active (e.g. during a
           quick sequence of add / quantity change / remove that all happen
           while the drawer stays open). That means the ORIGINAL listener
           from the first open() can end up firing later, against whatever
           DOM state exists at that moment — and renderContents() replaces
           #CartDrawer's entire innerHTML on every update, so the specific
           node this listener expects (.drawer__inner-empty or #CartDrawer)
           is not guaranteed to exist at the exact instant this fires. A
           null containerToTrapFocusOn made trapFocus()'s own
           getFocusableElements() throw (reading querySelectorAll on null),
           a real console error with no visible effect on the drawer's
           content. Guarding here is the minimal, safe fix: skip focus
           trapping for that one stale event instead of throwing. */
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    /* FIX (SOLCOZM Custom, 2026-08-10, causa reale confermata via ispezione
       diretta del DOM): questo metodo rimuoveva "is-empty" solo da
       .drawer__inner, ma nel markup di questo tema (snippets/cart-drawer.liquid)
       la classe "is-empty" viene applicata al tag <cart-drawer> ESTERNO
       (<cart-drawer class="drawer{% if cart == empty %} is-empty{% endif %}">),
       non a .drawer__inner. Le regole CSS dello stato "carrello vuoto"
       (cart-drawer.is-empty .drawer__inner { display:grid; grid-template-rows:1fr;
       align-items:center; } e cart-drawer.is-empty .drawer__header { display:none; })
       leggono anch'esse la classe sull'elemento ESTERNO. Di conseguenza, se il
       carrello era vuoto al primo caricamento pagina (classe "is-empty" impostata
       lato server sull'elemento esterno), questo metodo non la rimuoveva mai:
       ogni render successivo — anche con prodotto/nome/prezzo/subtotale/checkout
       correttamente presenti nell'HTML appena ricevuto — restava comunque
       vincolato al layout CSS dello stato vuoto (contenuto compresso/sovrapposto
       in un'unica riga di grid), dando l'impressione di un drawer vuoto/rotto
       nonostante il contenuto reale fosse già presente e corretto nel DOM.
       Fix: sincronizzare la classe "is-empty" dell'elemento ESTERNO con lo
       stato realmente riflesso dal contenuto appena iniettato (letto da
       cart-drawer-items, che porta la classe "is-empty" corretta calcolata
       dal server per QUESTA risposta), sia per aggiungerla che per
       rimuoverla, cosi' il layout resta sempre coerente con i dati reali. */
    const freshItems = this.querySelector('cart-drawer-items');
    const cartIsNowEmpty = freshItems ? freshItems.classList.contains('is-empty') : false;
    this.classList.toggle('is-empty', cartIsNowEmpty);

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.onclick = () => this.close();
      this.open(parsedState.triggeredBy);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
